import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Rate, Modal, Input, message, Spin, Empty, Tag, Space, List, Avatar } from 'antd';
import { StarOutlined, EditOutlined, DeleteOutlined, PlusOutlined, UserOutlined, ShoppingOutlined } from '@ant-design/icons';
import { reviewService, orderService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CustomerReviews = () => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewableItems, setReviewableItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, itemsRes] = await Promise.all([
        reviewService.getReviews({ customer_only: true }).catch(() => ({ data: [] })),
        reviewService.getReviewableItems().catch(() => ({ data: [] })),
      ]);
      const reviewsData = reviewsRes.data?.data?.data || reviewsRes.data?.data || reviewsRes.data || [];
      const itemsData = itemsRes.data?.data || itemsRes.data || [];
      setReviews(Array.isArray(reviewsData) ? reviewsData : []);
      setReviewableItems(Array.isArray(itemsData) ? itemsData : []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (item) => {
    setEditingReview(null);
    setFormData({ rating: 5, comment: '', product_service_id: item?.id, order_id: item?.order_id });
    setModalOpen(true);
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({ rating: review.rating || 5, comment: review.comment || '' });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.comment || formData.comment.trim().length < 5) {
      message.warning('Please write a comment (at least 5 characters)');
      return;
    }
    setSubmitting(true);
    try {
      if (editingReview) {
        await reviewService.updateReview(editingReview.id, { comment: formData.comment, rating: formData.rating });
        message.success('Review updated!');
      } else {
        await reviewService.createReview({ ...formData, comment: formData.comment });
        message.success('Review submitted!');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await reviewService.deleteReview(id);
      message.success('Review deleted');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  if (loading) return <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={2}>My Reviews</Title>
          <Paragraph>Manage your product and service reviews.</Paragraph>
        </div>
      </div>

      {reviewableItems.length > 0 && (
        <Card className="customer-card" title={<span><PlusOutlined /> Items You Can Review</span>} style={{ marginBottom: 24 }}>
          <List
            dataSource={reviewableItems.slice(0, 5)}
            renderItem={(item) => (
              <List.Item actions={[<Button type="primary" size="small" onClick={() => openCreateModal(item)} className="customer-btn-primary">Write Review</Button>]}>
                <List.Item.Meta avatar={<Avatar icon={<ShoppingOutlined />} style={{ backgroundColor: '#2d5a3d' }} />} title={item.name || item.product_service?.name || `Item #${item.id}`} description={`Order #${item.order_id || ''}`} />
              </List.Item>
            )}
          />
        </Card>
      )}

      {reviews.length === 0 ? (
        <Card><Empty description="You haven't written any reviews yet." /></Card>
      ) : (
        <Row gutter={[16, 16]}>
          {reviews.map((review) => (
            <Col xs={24} key={review.id}>
              <Card className="customer-card" actions={[
                <EditOutlined key="edit" onClick={() => openEditModal(review)} />,
                <DeleteOutlined key="delete" style={{ color: '#e5484d' }} onClick={() => { Modal.confirm({ title: 'Delete review?', onOk: () => handleDelete(review.id) }); }} />,
              ]}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Text strong style={{ color: '#2d5a3d', fontSize: 16 }}>{review.product_service?.name || review.product_name || `Review #${review.id}`}</Text>
                    <br />
                    <Rate disabled value={review.rating} style={{ fontSize: 14 }} />
                  </div>
                  <Tag color={review.is_approved ? 'green' : 'default'}>{review.is_approved ? 'Approved' : 'Pending'}</Tag>
                </div>
                <Paragraph style={{ marginTop: 8, color: '#374151' }}>{review.comment}</Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>{review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal title={editingReview ? 'Edit Review' : 'Write a Review'} open={modalOpen} onCancel={() => { setModalOpen(false); setFormData({ rating: 5, comment: '' }); }} onOk={handleSubmit} okText={editingReview ? 'Update' : 'Submit'} confirmLoading={submitting}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Text strong>Rating</Text>
          <br />
          <Rate value={formData.rating} onChange={(v) => setFormData((p) => ({ ...p, rating: v }))} style={{ fontSize: 28 }} />
        </div>
        <TextArea placeholder="Write your review..." rows={4} value={formData.comment} onChange={(e) => setFormData((p) => ({ ...p, comment: e.target.value }))} style={{ borderRadius: 10 }} />
      </Modal>
    </div>
  );
};

export default CustomerReviews;
